import { createContext, useCallback, useContext, useEffect, useMemo, useState, type AnchorHTMLAttributes, type MouseEvent, type ReactNode } from 'react';

interface Location {
  path: string;
  query: URLSearchParams;
}

interface RouterValue extends Location {
  navigate: (to: string, options?: { replace?: boolean }) => void;
  /** Changes query parameters on the current path (null removes one). */
  setQuery: (changes: Record<string, string | null>, options?: { replace?: boolean }) => void;
}

const RouterContext = createContext<RouterValue | null>(null);

const current = (): Location => ({ path: window.location.pathname, query: new URLSearchParams(window.location.search) });

/** A minimal history router: Studio has a handful of flat routes and needs nothing more. */
export function Router({ children }: { children: ReactNode }) {
  const [location, setLocation] = useState<Location>(current);

  useEffect(() => {
    const onPop = () => setLocation(current());
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  const navigate = useCallback((to: string, options: { replace?: boolean } = {}) => {
    if (options.replace) window.history.replaceState(null, '', to);
    else window.history.pushState(null, '', to);
    setLocation(current());
  }, []);

  const setQuery = useCallback(
    (changes: Record<string, string | null>, options: { replace?: boolean } = {}) => {
      const query = new URLSearchParams(window.location.search);
      for (const [key, value] of Object.entries(changes)) {
        if (value === null || value === '') query.delete(key);
        else query.set(key, value);
      }
      const search = query.toString();
      navigate(`${window.location.pathname}${search ? `?${search}` : ''}`, options);
    },
    [navigate],
  );

  const value = useMemo(() => ({ ...location, navigate, setQuery }), [location, navigate, setQuery]);
  return <RouterContext.Provider value={value}>{children}</RouterContext.Provider>;
}

export function useRouter(): RouterValue {
  const value = useContext(RouterContext);
  if (!value) throw new Error('useRouter outside Router');
  return value;
}

/** Matches `/merchants/:ref` style patterns; returns params or null. */
export function matchPath(pattern: string, path: string): Record<string, string> | null {
  const names: string[] = [];
  const regex = new RegExp(`^${pattern.replace(/:([a-zA-Z]+)/g, (_, name: string) => {
    names.push(name);
    return '([^/]+)';
  })}/?$`);
  const match = regex.exec(path);
  if (!match) return null;
  return Object.fromEntries(names.map((name, index) => [name, decodeURIComponent(match[index + 1] ?? '')]));
}

export function Link({ to, children, ...rest }: { to: string; children: ReactNode } & AnchorHTMLAttributes<HTMLAnchorElement>) {
  const { navigate } = useRouter();
  const onClick = (event: MouseEvent<HTMLAnchorElement>) => {
    rest.onClick?.(event);
    if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
    event.preventDefault();
    navigate(to);
  };
  return (
    <a href={to} {...rest} onClick={onClick}>
      {children}
    </a>
  );
}
